import type { TrustAssessment, TrustStatus, Validity } from '@upl/evidence-schema';

export interface TrustPolicy {
  name: string;
  version: string;
  trustedSignerIds: readonly string[];
  knownSignerIds: readonly string[];
}

export interface TrustInput {
  evaluatedAt: string;
  signatureValidity: Validity;
  certificateChainValidity: Validity;
  signerId?: string;
  signerIdentity?: string;
  credentialExpired: boolean;
  credentialRevoked?: boolean;
  policyTrustDecision?: Validity;
}

export function evaluateTrust(input: TrustInput, policy: TrustPolicy): TrustAssessment {
  const statuses = new Set<TrustStatus>();

  if (input.signatureValidity === 'VALID') statuses.add('VALID_SIGNATURE');
  if (input.signatureValidity === 'INVALID') statuses.add('INVALID_SIGNATURE');
  if (input.certificateChainValidity === 'INVALID') statuses.add('UNTRUSTED_CHAIN');
  if (input.credentialExpired) statuses.add('EXPIRED_CREDENTIAL');
  if (input.credentialRevoked === true) statuses.add('REVOKED');

  if (input.policyTrustDecision === 'VALID') {
    statuses.add('TRUSTED_SIGNER');
    statuses.add('KNOWN_SIGNER');
  }

  if (input.policyTrustDecision === 'VALID') {
    // The upstream verifier made a reproducible policy decision.
  } else if (input.signerId && policy.trustedSignerIds.includes(input.signerId)) {
    statuses.add('TRUSTED_SIGNER');
    statuses.add('KNOWN_SIGNER');
  } else if (input.signerId && policy.knownSignerIds.includes(input.signerId)) {
    statuses.add('KNOWN_SIGNER');
  } else if (input.signerId) {
    statuses.add('UNKNOWN_SIGNER');
  }

  if (statuses.size === 0) statuses.add('TRUST_STATUS_UNKNOWN');

  return {
    policy_name: policy.name,
    policy_version: policy.version,
    evaluated_at: input.evaluatedAt,
    statuses: [...statuses],
    ...(input.signerIdentity ? { signer_identity: input.signerIdentity } : {}),
    certificate_chain_validity: input.certificateChainValidity,
    trust_list_membership: input.policyTrustDecision ?? (input.signerId
      ? policy.trustedSignerIds.includes(input.signerId)
        ? 'VALID'
        : 'INVALID'
      : 'UNKNOWN'),
  };
}
